import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../../../utils/axios';
import { ActionLogResponse, ActionLogFilters } from '../../../@types/actionLog/actionLog.type';

interface ActionLogState {
  logs: ActionLogResponse[];
  actionTypes: string[];
  isLoading: boolean;
  error: string | null;
  totalPages: number;
  totalElements: number;
  page: number;
  size: number;
}

const initialState: ActionLogState = {
  logs: [],
  actionTypes: [],
  isLoading: false,
  error: null,
  totalPages: 0,
  totalElements: 0,
  page: 0,
  size: 20,
};

export const fetchActionLogs = createAsyncThunk(
  'actionLogs/fetchActionLogs',
  async (filters: ActionLogFilters, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/v1/action-logs', { params: filters });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch action logs');
    }
  }
);

export const fetchActionTypes = createAsyncThunk(
  'actionLogs/fetchActionTypes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get('/api/v1/action-logs/action-types');
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch action types');
    }
  }
);

export const deleteActionLog = createAsyncThunk(
  'actionLogs/deleteActionLog',
  async (id: string, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/v1/action-logs/${id}`);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete action log');
    }
  }
);

const actionLogSlice = createSlice({
  name: 'actionLogs',
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setSize: (state, action: PayloadAction<number>) => {
      state.size = action.payload;
      state.page = 0; // reset page when size changes
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActionLogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchActionLogs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.logs = action.payload.content || [];
        state.totalPages = action.payload.totalPages || 0;
        state.totalElements = action.payload.totalElements || 0;
        state.page = action.payload.pageable?.pageNumber || 0;
      })
      .addCase(fetchActionLogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchActionTypes.fulfilled, (state, action) => {
        state.actionTypes = action.payload;
      })
      .addCase(deleteActionLog.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteActionLog.fulfilled, (state, action) => {
        state.isLoading = false;
        state.logs = state.logs.filter((log) => log.id !== action.payload);
        state.totalElements = Math.max(0, state.totalElements - 1);
      })
      .addCase(deleteActionLog.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPage, setSize } = actionLogSlice.actions;

export default actionLogSlice.reducer;

